// @flow
import React, { type Node, Fragment, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import CSSSticky from 'app/common/ui-components/css-sticky';
import { RowLayoutWithSticky, RowLayoutContent } from 'app/common/layouts/RowLayout';
import SimpleDate from 'app/dataProviders/SimpleDate';
import type { Klass } from 'app/dataTypes/Klasses.types';

import ControlsLayout from '../ControlsLayout';
import JournalHeader from './JournalHeader';
import JournalHeaderSummary from './JournalHeaderSummary';
import usePeriods from './usePeriods';
import MenuLinePeriods from './MenuLinePeriods';
import MenuLineMonths from './MenuLineMonths';
import MarksThemeSwitcher from './MarksThemeSwitcher';
import { MarksThemeProvider, themeTypes } from '../MarkList';
import TableMarks from './TableMarks';
import TableSummary from './TableSummary';
import useDisciplines from './useDisciplines';

import styles from './JournalMain.scss';


type Props = {
  children?: Node,
  studentId: string,
  date: SimpleDate,
  klass: Klass,
  additionalControls?: Node,
  showSummary: boolean,
  createPath: ({ date?: SimpleDate, summary: boolean }) => string,
}

const JournalMain = ({
  children,
  studentId,
  date,
  klass,
  additionalControls,
  showSummary,
  createPath,
}: Props): Node => {
  const { t } = useTranslation('dnevnik');
  const themeSwitcherLabel = t('marks_type_switcher_label');
  const [theme, setTheme] = useState(themeTypes.COLORED);
  const switchToBW = useCallback(() => setTheme(themeTypes.BW), []);
  const switchToColor = useCallback(() => setTheme(themeTypes.COLORED), []);

  const [disciplines, setDisciplines] = useDisciplines();

  const [periods, currentPeriod] = usePeriods(klass, date, createPath);
  const currentPeriodTitle = currentPeriod ? currentPeriod.title : '???';

  const summaryProps = useMemo(() => ({
    title: t('total_header'),
    link: createPath({ date, summary: true }),
    current: showSummary,
  }), [createPath, date, showSummary, t]);

  const headerContent = (
    showSummary

      ? <Fragment>
        <RowLayoutContent>
          <JournalHeaderSummary
            studentId={studentId}
            klass={klass}
          />
          <MenuLinePeriods
            className={styles.periodsMenu}
            periods={periods}
            additionalMenuItem={summaryProps}
          />
        </RowLayoutContent>
      </Fragment>

      : <Fragment>
        <RowLayoutContent>
          <JournalHeader
            studentId={studentId}
            klass={klass}
            date={date}
            currentPeriodTitle={currentPeriodTitle}
          />
          <MenuLinePeriods
            className={styles.periodsMenu}
            periods={periods}
            additionalMenuItem={summaryProps}
          />
        </RowLayoutContent>
        <RowLayoutContent flex>
          <MenuLineMonths
            className={styles.monthsMenu}
            period={currentPeriod}
            createPath={createPath}
            date={date}
          />
          <MarksThemeSwitcher
            label={themeSwitcherLabel}
            switchToBW={switchToBW}
            switchToColor={switchToColor}
          />
        </RowLayoutContent>
      </Fragment>
  );

  return (
    <Fragment>
      <CSSSticky
        number={1}
        className={styles.stickable}
      >
        <ControlsLayout leftContent={additionalControls}>
          {children || null}
        </ControlsLayout>
      </CSSSticky>

      <RowLayoutWithSticky
        number={2}
        content={headerContent}
      >
        <MarksThemeProvider theme={theme}>
          {showSummary
            ? <TableSummary
              periods={periods}
              date={date}
              studentId={studentId}
              klassId={klass.id}
              disciplines={disciplines}
              onUpdateDIsciplines={setDisciplines}
            />
            : <TableMarks
              date={date}
              studentId={studentId}
              klassId={klass.id}
              disciplines={disciplines}
              onUpdateDIsciplines={setDisciplines}
            />}
        </MarksThemeProvider>
      </RowLayoutWithSticky>
    </Fragment>
  );
};

export default JournalMain;
